import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  fetchUser,
  selectUserCallStatus,
  selectUserObject,
} from '../../features/user/userSlice'
import {
  convertPhoneToE164WithCountryCode,
  findCountryInNumber,
  CheckPhoneNumber,
  formatPhoneToNationalWithCountry
} from '../../util/phoneUtils'
import {
  updatePhoneInfo,
  selectUserPhone,
  deletePhoneNumber,
  submitPhoneNumber,
  fetchPhoneNumber,
} from './userPhoneSlice'
import '../../util/i18n'

const EditPhoneManager = () => {
  const dispatch = useDispatch()
  const userObj = useSelector(selectUserObject)
  const userCallStatus = useSelector(selectUserCallStatus)
  const [userHasNumber, setUserHasNumber] = useState(false)
  const [isAPhoneNumber, setIsAPhoneNumber] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [supportText, setSupportText] = useState('')
  const [isTouched, setIsTouched] = useState(false)
  const [triedValidate, settriedValidate] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const {
    currentCountry,
    currentCountryPhoneCode,
    phoneNumber,
    phoneField,
    status
  } = useSelector(selectUserPhone)
  const phoneEditInitialized = sessionStorage.getItem('phoneEditInitialized')
  const [isSaving, setIsSaving] = useState(false)
  const navigate = useNavigate()

  const removeSessionStorage = () => {
    sessionStorage.removeItem('phoneEditInitialized')
  }

  useEffect(() => {
    if (phoneEditInitialized) return

    if (status === 'idle') {
      dispatch(fetchPhoneNumber(' '))
    }
  }, [status])

  useEffect(() => {
    if (!phoneEditInitialized && phoneNumber) {
      const phoneArray = findCountryInNumber(phoneNumber)
      if (phoneArray && phoneArray.length > 0) {
        dispatch(
          updatePhoneInfo({
            currentCountry: phoneArray[0]?.number.country,
            currentCountryPhoneCode: `+${phoneArray[0]?.number.countryCallingCode}`,
            phoneNumber,
            phoneField,
            userObj,
          })
        )
      }
      setIsAPhoneNumber(CheckPhoneNumber(phoneField, currentCountry))
      sessionStorage.setItem('phoneEditInitialized', true)
      window.addEventListener('beforeunload', removeSessionStorage)
    }
  }, [phoneField])

  useEffect(() => {
    if (userCallStatus === 'idle') {
      dispatch(fetchUser())
      removeSessionStorage()
    }
  }, [userCallStatus, dispatch])

  useEffect(() => {
    const phoneArray = findCountryInNumber(phoneNumber)
    if (phoneArray && phoneArray.length > 0) {
      setUserHasNumber(true)
    }
  }, [phoneField])

  useEffect(() => {
    const handleResize = () => {
      if (phoneNumber && currentCountry) {
        setIsAPhoneNumber(CheckPhoneNumber(phoneField, currentCountry))
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [phoneField, currentCountry])

  const handleError = (error) => {
    const errorCode = error.code
    let message = ''

    switch (errorCode) {
      case 'METHOD_NOT_ALLOWED':
        message = 'error-mfa-phone-not-allowed'
        break
      default:
        message = 'Failed to add the phone number'
    }

    setHasError(true)
    setErrorMessage(message)
  }

  const handleSubmit = () => {
    setIsSaving(true)
    const formattedPhone = convertPhoneToE164WithCountryCode(
      phoneField,
      currentCountryPhoneCode
    )
    dispatch(submitPhoneNumber(formattedPhone))
      .unwrap()
      .then(() => {
        navigate('/verify-phone')
        setIsAPhoneNumber(true)
        setUserHasNumber(true)
        removeSessionStorage()
      })
      .catch((err) => handleError(err))
      .finally(() => {
        setIsSaving(false)
      })
  }

  const handleChange = (value) => {
    const formatted = formatPhoneToNationalWithCountry(
      value,
      currentCountry
    )
    dispatch(
      updatePhoneInfo({
        currentCountryPhoneCode,
        currentCountry,
        phoneField: formatted
      })
    )

    const isPhone = CheckPhoneNumber(value, currentCountry)
    setIsAPhoneNumber(isPhone)
    setIsTouched(true)
  }

  const handleCountryCode = (country) => {
    formatPhoneToNationalWithCountry(
      phoneField,
      country
    )
    dispatch(
      updatePhoneInfo({
        currentCountry: country.code,
        currentCountryPhoneCode: country.phoneCode,
        phoneField,
        userObj,
      })
    )
    setIsAPhoneNumber(CheckPhoneNumber(phoneField, country.code))
  }

  const handleDelete = () => {
    setIsDeleting(true)
    dispatch(deletePhoneNumber(phoneNumber))
      .unwrap()
      .then(() => {
        setUserHasNumber(false)
        dispatch(
          updatePhoneInfo({
            currentCountry: '',
            currentCountryPhoneCode: '+',
            phoneNumber: '',
            formattedPhone: '',
            phoneField: '',
            userObj,
          })
        )
        removeSessionStorage()
      })
      .catch(() => { })
      .finally(() => {
        setIsDeleting(false)
        setIsAPhoneNumber(false)
      })
  }

  return {
    handleSubmit,
    phoneNumber,
    handleChange,
    currentCountry,
    currentCountryPhoneCode,
    handleCountryCode,
    removeSessionStorage,
    isAPhoneNumber,
    userHasNumber,
    handleDelete,
    isDeleting,
    supportText,
    setSupportText,
    isTouched,
    setIsTouched,
    triedValidate,
    hasError,
    errorMessage,
    settriedValidate,
    isSaving,
    setIsAPhoneNumber
  }
}

export default EditPhoneManager
